import React, { useState } from "react";
import PropTypes from "prop-types";
import { useMutation, gql } from "@apollo/client";
import { useCurrentUser } from "hooks";
import getTrackingCookie from "util/adwords";
import useToast from "hooks/useToast";
import { getCookie } from "util/cookies";
import ContextualSignUpModal from "components/componentLibrary/Modals/ContextualSignUp";

const ADD_INQUIRY_MUTATION = gql`
  mutation AddInquiryMutation(
    $email: String!
    $inquiribleId: Int!
    $source: String
    $gclid: String
    $serverId: String
    $googleAnalyticsId: String
    $signUpType: String
    $availableTimes: [AvailableTimes]
    $inquiribleType: InquiribleType
  ) {
    addInquiry(
      input: {
        renter: {
          email: $email
          serverId: $serverId
          gclid: $gclid
          signUpType: $signUpType
          googleAnalyticsId: $googleAnalyticsId
        }
        inquiribleId: $inquiribleId
        inquiribleType: $inquiribleType
        onboardingInfo: { source: $source }
        availableTimes: $availableTimes
      }
    ) {
      created
      currentUser {
        id
        uuid
        email
        phone
        isAdmin
        lastName
        isRenter
        isBroker
        firstName
        e164Phone
        isVerified
        companyName
        displayName
        hasPassword
        salesforceId
        featureFlags
        profilePicURL
        completedUserTasks {
          id
          task
        }
        confirmed
        cityOfInterest
        unconfirmedEmail
        tours {
          id
          date
        }
        latestCampaign {
          id
          uuid
          listings {
            id
          }
          currentActiveRequirements(setByUser: true) {
            id
            neighborhoods {
              id
              name
            }
            minPsf
            maxPsf
            minSquareFeet
            maxSquareFeet
            minMonthlyBudget
            maxMonthlyBudget
            numberOfEmployees
            minSqft: minSquareFeet
            maxSqft: maxSquareFeet
            maxPricePerSquareFoot: maxPsf
            minPricePerSquareFoot: minPsf
            maxMonthlyPrice: maxMonthlyBudget
            minMonthlyPrice: minMonthlyBudget
            leaseTerm
            areas {
              id
              externalId
              name
            }
            market {
              id
              name
              externalId
              position {
                lat
                lng
              }
            }
          }
        }
      }
    }
  }
`;

const SaveWrapper = ({
  children,
  inquiribleId,
  signUpType,
  source,
  type,
  areaExternalIds,
  marketOfInterest,
  cityOfInterest,
  leaseTermMin,
  leaseTermMax,
  minPricePerSquareFoot,
  maxPricePerSquareFoot,
  minSqft,
  maxSqft,
  minMonthlyPrice,
  maxMonthlyPrice,
  numEmployees,
  phoneRequired,
  onSuccess,
  availableTimes,
}) => {
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [addInquiry, { loading }] = useMutation(ADD_INQUIRY_MUTATION);
  const { currentUser /* , refreshCurrentUser */ } = useCurrentUser();
  const { showToast } = useToast();

  const isSaved =
    type === "Listing" &&
    currentUser?.latestCampaign?.listings
      .map((listing) => listing.id)
      .includes(inquiribleId);

  const closeInquiryModal = () => setShowInquiryModal(false);

  const saveListing = () => {
    if (currentUser) {
      addInquiry({
        variables: {
          email: currentUser.email,
          inquiribleId: parseInt(inquiribleId, 10),
          inquiribleType: type.toUpperCase(),
          source,
          availableTimes,
          gclid: getTrackingCookie(),
          serverId: getCookie("SERVERID")
            ? getCookie("SERVERID")
            : "unidentified",
          googleAnalyticsId: getCookie("_gid"),
          signUpType,
        },
      }).then(({ data: result }) => {
        if (result?.addInquiry?.currentUser) {
          // refreshCurrentUser();
          const toastMessage =
            availableTimes.length > 0
              ? `${type} saved! A broker will be in contact soon to arrange a tour time.`
              : `${type} saved! You can view saved ${type.toLowerCase()}s in your tourbook.`;
          showToast(toastMessage, 5000);
        }
        onSuccess(false);
      });
      closeInquiryModal();
    } else {
      setShowInquiryModal(true);
    }
  };

  const onSaveListing = (e) => {
    e.preventDefault();
    e.stopPropagation();
    saveListing();
  };

  return (
    <>
      {children(onSaveListing, isSaved, loading)}
      {showInquiryModal && (
        <ContextualSignUpModal
          inquiribleId={inquiribleId}
          onClose={closeInquiryModal}
          source={source}
          signUpType={signUpType}
          type={type}
          areaExternalIds={areaExternalIds}
          marketOfInterest={marketOfInterest}
          cityOfInterest={cityOfInterest}
          leaseTermMin={leaseTermMin}
          leaseTermMax={leaseTermMax}
          minPricePerSquareFoot={minPricePerSquareFoot}
          maxPricePerSquareFoot={maxPricePerSquareFoot}
          minSqft={minSqft}
          maxSqft={maxSqft}
          minMonthlyPrice={minMonthlyPrice}
          maxMonthlyPrice={maxMonthlyPrice}
          numEmployees={numEmployees}
          phoneRequired={phoneRequired}
          onSuccess={onSuccess}
          availableTimes={availableTimes}
        />
      )}
    </>
  );
};

SaveWrapper.propTypes = {
  inquiribleId: PropTypes.string.isRequired,
  children: PropTypes.func.isRequired,
  signUpType: PropTypes.string,
  source: PropTypes.string,
  type: PropTypes.oneOf(["Listing", "Building"]),
  availableTimes: PropTypes.arrayOf(PropTypes.string),
  areaExternalIds: PropTypes.arrayOf(PropTypes.string),
  marketOfInterest: PropTypes.string,
  cityOfInterest: PropTypes.string,
  leaseTermMin: PropTypes.number,
  leaseTermMax: PropTypes.number,
  minPricePerSquareFoot: PropTypes.number,
  maxPricePerSquareFoot: PropTypes.number,
  minSqft: PropTypes.number,
  maxSqft: PropTypes.number,
  minMonthlyPrice: PropTypes.number,
  maxMonthlyPrice: PropTypes.number,
  numEmployees: PropTypes.string,
  phoneRequired: PropTypes.bool,
  onSuccess: PropTypes.func,
};

SaveWrapper.defaultProps = {
  signUpType: null,
  source: "Save Listing",
  type: "Listing",
  availableTimes: [],
  areaExternalIds: [],
  marketOfInterest: null,
  cityOfInterest: null,
  leaseTermMin: null,
  leaseTermMax: null,
  minPricePerSquareFoot: null,
  maxPricePerSquareFoot: null,
  minSqft: null,
  maxSqft: null,
  minMonthlyPrice: null,
  maxMonthlyPrice: null,
  numEmployees: null,
  phoneRequired: false,
  // eslint-disable-next-line no-unused-vars
  onSuccess: (visitSpace) => {},
};

export default SaveWrapper;
